import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import {
  transactionService,
  brokerService,
  storageService,
  pendingRegistrationService,
  whitelistedAccountService,
  aiSettingsService,
  serialNumberService,
  cleanupService
} from '../services/firebaseService';

const AppContext = createContext();

// Pakistani Banks & Digital Wallets List (Alphabetically Sorted)
export const PAKISTAN_BANKS = [
  { code: 'ALBARAKA', name: 'Al Baraka Bank', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/albaraka.png' },
  { code: 'ABL', name: 'Allied Bank Limited (ABL)', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/abl.jpeg' },
  { code: 'ASKARI', name: 'Askari Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/askari.png' },
  { code: 'BAHL', name: 'Bank AL Habib Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/alhabib.png' },
  { code: 'BAFL', name: 'Bank Alfalah Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/alfalah.png' },
  { code: 'BANKISLAMI', name: 'BankIslami Pakistan Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/bankislami.png' },
  { code: 'DUBAI', name: 'Dubai Islamic Bank Pakistan Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/dubai.jpeg' },
  { code: 'EASYPAISA', name: 'Easypaisa (Telenor Bank)', ibanPrefix: 'PK', type: 'wallet', logo: '/bank-logos/easypaisa.jpeg' },
  { code: 'FAYSAL', name: 'Faysal Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/faysal.jpeg' },
  { code: 'FWBL', name: 'First Women Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/fwbl.png' },
  { code: 'HBL', name: 'Habib Bank Limited (HBL)', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/hbl.png' },
  { code: 'HMB', name: 'Habib Metropolitan Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/hmb.png' },
  { code: 'JAZZCASH', name: 'JazzCash (Mobilink Bank)', ibanPrefix: 'PK', type: 'wallet', logo: '/bank-logos/jazzcash.png' },
  { code: 'JSBL', name: 'JS Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/jsbl.png' },
  { code: 'MCB', name: 'MCB Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/mcb.png' },
  { code: 'MCBISLAMI', name: 'MCB Islamic Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/mcbislami.jpeg' },
  { code: 'MEEZAN', name: 'Meezan Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/meezan.png' },
  { code: 'NBP', name: 'National Bank of Pakistan (NBP)', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/nbp.jpeg' },
  { code: 'NAYAPAY', name: 'NayaPay', ibanPrefix: 'PK', type: 'wallet', logo: '/bank-logos/nayapay.jpeg' },
  { code: 'PPCBL', name: 'Punjab Provincial Cooperative Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/ppcbl.jpeg' },
  { code: 'SADAPAY', name: 'SadaPay', ibanPrefix: 'PK', type: 'wallet', logo: '/bank-logos/sadapay.png' },
  { code: 'SAMBA', name: 'Samba Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/samba.png' },
  { code: 'SILK', name: 'Silkbank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/silk.webp' },
  { code: 'SINDH', name: 'Sindh Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/sindh.png' },
  { code: 'SONERI', name: 'Soneri Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/soneri.jpeg' },
  { code: 'SCB', name: 'Standard Chartered Bank (Pakistan) Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/scb.png' },
  { code: 'SUMMIT', name: 'Summit Bank', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/summit.png' },
  { code: 'BOK', name: 'The Bank of Khyber', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/bok.png' },
  { code: 'BOP', name: 'The Bank of Punjab', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/bop.png' },
  { code: 'UBL', name: 'United Bank Limited (UBL)', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/ubl.png' },
  { code: 'ZTBL', name: 'Zarai Taraqiati Bank Limited', ibanPrefix: 'PK', type: 'bank', logo: '/bank-logos/ztbl.jpg' },
];

// Broker-specific Bank Accounts (where users send payments)
export const BROKER_BANK_ACCOUNTS = {
  savexa: {
    bankName: 'MCB Bank Limited',
    accountTitle: 'Savexa',
    accountNumber: '1680500581000024',
    iban: 'PK69MUCB1680500581000024',
    branchName: 'Saidpur Road Branch',
  },
  justmarkets: {
    bankName: 'JS Bank Limited',
    accountTitle: 'Just Markets',
    accountNumber: '2898053',
    iban: 'PK43JSBL9603000002898053',
    branchName: 'Saidpur Road Branch',
  },
};

// Helper function to get bank account by broker ID
export const getBrokerBankAccount = (brokerId) => {
  return BROKER_BANK_ACCOUNTS[brokerId?.toLowerCase()] || BROKER_BANK_ACCOUNTS.savexa;
};

// Initial State
const initialState = {
  // User session data - Updated for new flow
  currentTransaction: null,
  verificationStatus: null,
  referenceNumber: null,
  otpCode: null,
  otpExpiry: null,

  // Serial number counter for reference numbers
  lastSerialNumber: 0,

  // User type detection
  userType: null, // 'new', 'returning', 'platform'
  isLoggedInFromPlatform: false, // If user came from main platform (skip CNIC/OTP)

  // Whitelisted accounts storage (persisted)
  whitelistedAccounts: [], // All whitelisted accounts across users

  // Pending account registrations (waiting for admin approval)
  pendingAccountRegistrations: [],

  // Manual review queue
  manualReviewQueue: [],

  // Admin data
  isAdminAuthenticated: false,
  adminUser: null,

  // Broker data (would come from broker API in production)
  // Demo users for Savexa and JustMarkets portals
  brokerUsers: [
    // ========== SAVEXA USERS ==========
    {
      id: 'SV001',
      name: 'Muhammad Ahmed Khan',
      cnic: '35201-1234567-1',
      broker: 'Savexa',
      phone: '+923003596645',
      email: 'alieeestan@gmail.com',
      address: 'House 45, Street 12, Gulberg III, Lahore',
      cnicExpiry: '15-03-2028',
      kycVerified: true,
      cnicFrontImage: 'https://via.placeholder.com/600x380/1e40af/ffffff?text=CNIC+FRONT+-+Ahmed',
      cnicBackImage: 'https://via.placeholder.com/600x380/1e40af/ffffff?text=CNIC+BACK+-+Ahmed',
    },
    {
      id: 'SV002',
      name: 'Babar Ali',
      cnic: '42301-9876543-2',
      broker: 'Savexa',
      phone: '+971561110052',
      email: 'babar08880@gmail.com',
      address: 'Flat 302, Al Nahda Tower, Dubai, UAE',
      cnicExpiry: '22-11-2027',
      kycVerified: true,
      cnicFrontImage: 'https://via.placeholder.com/600x380/1e40af/ffffff?text=CNIC+FRONT+-+Babar',
      cnicBackImage: 'https://via.placeholder.com/600x380/1e40af/ffffff?text=CNIC+BACK+-+Babar',
    },
    // ========== JUSTMARKETS USERS ==========
    {
      id: 'JM001',
      name: 'Muhammad Ahmed Khan',
      cnic: '35201-1234567-1',
      broker: 'JustMarkets',
      phone: '+923003596645',
      email: 'alieeestan@gmail.com',
      address: 'House 45, Street 12, Gulberg III, Lahore',
      cnicExpiry: '15-03-2028',
      kycVerified: true,
      cnicFrontImage: 'https://via.placeholder.com/600x380/059669/ffffff?text=CNIC+FRONT+-+Ahmed',
      cnicBackImage: 'https://via.placeholder.com/600x380/059669/ffffff?text=CNIC+BACK+-+Ahmed',
    },
    {
      id: 'JM002',
      name: 'Babar Ali',
      cnic: '42301-9876543-2',
      broker: 'JustMarkets',
      phone: '+971561110052',
      email: 'babar08880@gmail.com',
      address: 'Flat 302, Al Nahda Tower, Dubai, UAE',
      cnicExpiry: '22-11-2027',
      kycVerified: true,
      cnicFrontImage: 'https://via.placeholder.com/600x380/059669/ffffff?text=CNIC+FRONT+-+Babar',
      cnicBackImage: 'https://via.placeholder.com/600x380/059669/ffffff?text=CNIC+BACK+-+Babar',
    },
  ],

  // Transactions
  transactions: [],

  // Brokers
  brokers: [
    { id: 'BR001', name: 'ABC Exchange', apiKey: 'abc-xxx-123', status: 'active', usersCount: 150, createdAt: '2023-06-15' },
    { id: 'BR002', name: 'XYZ Broker', apiKey: 'xyz-xxx-456', status: 'active', usersCount: 89, createdAt: '2023-08-20' },
    { id: 'BR003', name: 'DEF Trading', apiKey: 'def-xxx-789', status: 'inactive', usersCount: 45, createdAt: '2023-10-10' },
  ],

  // Target bank accounts for payment
  targetAccounts: [
    { id: 'TA001', bankName: 'HBL', accountNumber: '00112233445566', accountTitle: 'ANB TRADING', branchCode: '0001' },
    { id: 'TA002', bankName: 'MCB', accountNumber: '99887766554433', accountTitle: 'ANB TRADING', branchCode: '0002' },
  ],

  // Notifications/Messages
  notifications: [],

  // Loading states
  isLoading: true,
  error: null,

  // Data loaded flags - track which data has actually been fetched
  dataLoaded: {
    transactions: false,
    pendingRegistrations: false,
    whitelistedAccounts: false,
    aiSettings: false,
  },

  // Firebase connection status
  firebaseConnected: false,

  // AI Settings
  aiConfigs: [],
  activeAIConfig: null,
};

// Action Types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CURRENT_TRANSACTION: 'SET_CURRENT_TRANSACTION',
  SET_VERIFICATION_STATUS: 'SET_VERIFICATION_STATUS',
  SET_REFERENCE_NUMBER: 'SET_REFERENCE_NUMBER',
  SET_OTP_CODE: 'SET_OTP_CODE',
  SET_TRANSACTIONS: 'SET_TRANSACTIONS',
  ADD_TRANSACTION: 'ADD_TRANSACTION',
  UPDATE_TRANSACTION: 'UPDATE_TRANSACTION',
  SET_BROKERS: 'SET_BROKERS',
  LOGIN_ADMIN: 'LOGIN_ADMIN',
  LOGOUT_ADMIN: 'LOGOUT_ADMIN',
  ADD_BROKER: 'ADD_BROKER',
  UPDATE_BROKER: 'UPDATE_BROKER',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION',
  RESET_USER_SESSION: 'RESET_USER_SESSION',
  SET_FIREBASE_CONNECTED: 'SET_FIREBASE_CONNECTED',
  // New action types for complete flow
  ADD_WHITELISTED_ACCOUNT: 'ADD_WHITELISTED_ACCOUNT',
  SET_WHITELISTED_ACCOUNTS: 'SET_WHITELISTED_ACCOUNTS',
  UPDATE_WHITELISTED_ACCOUNT: 'UPDATE_WHITELISTED_ACCOUNT',
  SET_USER_TYPE: 'SET_USER_TYPE',
  SET_PLATFORM_LOGIN: 'SET_PLATFORM_LOGIN',
  ADD_PENDING_REGISTRATION: 'ADD_PENDING_REGISTRATION',
  SET_PENDING_REGISTRATIONS: 'SET_PENDING_REGISTRATIONS',
  APPROVE_PENDING_REGISTRATION: 'APPROVE_PENDING_REGISTRATION',
  REJECT_PENDING_REGISTRATION: 'REJECT_PENDING_REGISTRATION',
  DELETE_PENDING_REGISTRATION: 'DELETE_PENDING_REGISTRATION',
  ADD_TO_MANUAL_REVIEW: 'ADD_TO_MANUAL_REVIEW',
  RESOLVE_MANUAL_REVIEW: 'RESOLVE_MANUAL_REVIEW',
  DELETE_WHITELISTED_ACCOUNT: 'DELETE_WHITELISTED_ACCOUNT',
  // AI Settings
  SET_AI_CONFIGS: 'SET_AI_CONFIGS',
  ADD_AI_CONFIG: 'ADD_AI_CONFIG',
  UPDATE_AI_CONFIG: 'UPDATE_AI_CONFIG',
  DELETE_AI_CONFIG: 'DELETE_AI_CONFIG',
  SET_ACTIVE_AI_CONFIG: 'SET_ACTIVE_AI_CONFIG',
  // Broker Users
  UPDATE_BROKER_USER: 'UPDATE_BROKER_USER',
  // Data loaded tracking
  SET_DATA_LOADED: 'SET_DATA_LOADED',
  // Serial number
  INCREMENT_SERIAL_NUMBER: 'INCREMENT_SERIAL_NUMBER',
  SET_SERIAL_NUMBER: 'SET_SERIAL_NUMBER',
};

// Reducer
function appReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload };

    case ActionTypes.SET_CURRENT_TRANSACTION:
      return { ...state, currentTransaction: action.payload };

    case ActionTypes.SET_VERIFICATION_STATUS:
      return { ...state, verificationStatus: action.payload };

    case ActionTypes.SET_REFERENCE_NUMBER:
      return { ...state, referenceNumber: action.payload };

    case ActionTypes.INCREMENT_SERIAL_NUMBER:
      return { ...state, lastSerialNumber: state.lastSerialNumber + 1 };

    case ActionTypes.SET_SERIAL_NUMBER:
      return { ...state, lastSerialNumber: action.payload };

    case ActionTypes.SET_OTP_CODE:
      return {
        ...state,
        otpCode: action.payload.code,
        otpExpiry: action.payload.expiry
      };

    case ActionTypes.SET_TRANSACTIONS:
      // Also mark transactions as loaded when setting data
      const txnDataLoaded = {
        ...state.dataLoaded,
        transactions: true,
      };
      const txnAllLoaded = Object.values(txnDataLoaded).every(v => v === true);
      if (txnAllLoaded) {
        console.log('[Loading] All data ready - stopping spinner');
      }
      return {
        ...state,
        transactions: action.payload,
        dataLoaded: txnDataLoaded,
        isLoading: txnAllLoaded ? false : state.isLoading,
      };

    case ActionTypes.ADD_TRANSACTION:
      return { ...state, transactions: [...state.transactions, action.payload] };

    case ActionTypes.SET_BROKERS:
      return { ...state, brokers: action.payload };

    case ActionTypes.UPDATE_TRANSACTION:
      return {
        ...state,
        transactions: state.transactions.map(t =>
          t.id === action.payload.id ? { ...t, ...action.payload } : t
        ),
      };

    case ActionTypes.LOGIN_ADMIN:
      return { ...state, isAdminAuthenticated: true, adminUser: action.payload };

    case ActionTypes.LOGOUT_ADMIN:
      return { ...state, isAdminAuthenticated: false, adminUser: null };

    case ActionTypes.ADD_BROKER:
      return { ...state, brokers: [...state.brokers, action.payload] };

    case ActionTypes.UPDATE_BROKER:
      return {
        ...state,
        brokers: state.brokers.map(b =>
          b.id === action.payload.id ? { ...b, ...action.payload } : b
        ),
      };

    case ActionTypes.ADD_NOTIFICATION:
      return { ...state, notifications: [...state.notifications, action.payload] };

    case ActionTypes.CLEAR_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
      };

    case ActionTypes.RESET_USER_SESSION:
      return {
        ...state,
        currentTransaction: null,
        verificationStatus: null,
        referenceNumber: null,
        otpCode: null,
        otpExpiry: null,
      };

    case ActionTypes.SET_FIREBASE_CONNECTED:
      return { ...state, firebaseConnected: action.payload };

    case ActionTypes.ADD_WHITELISTED_ACCOUNT:
      return {
        ...state,
        whitelistedAccounts: [...state.whitelistedAccounts, action.payload],
      };

    case ActionTypes.SET_WHITELISTED_ACCOUNTS:
      // Also mark whitelisted accounts as loaded when setting data
      const whitelistDataLoaded = {
        ...state.dataLoaded,
        whitelistedAccounts: true,
      };
      const whitelistAllLoaded = Object.values(whitelistDataLoaded).every(v => v === true);
      if (whitelistAllLoaded) {
        console.log('[Loading] All data ready - stopping spinner');
      }
      return {
        ...state,
        whitelistedAccounts: action.payload,
        dataLoaded: whitelistDataLoaded,
        isLoading: whitelistAllLoaded ? false : state.isLoading,
      };

    case ActionTypes.UPDATE_WHITELISTED_ACCOUNT:
      return {
        ...state,
        whitelistedAccounts: state.whitelistedAccounts.map(acc =>
          acc.id === action.payload.id ? { ...acc, ...action.payload } : acc
        ),
      };

    case ActionTypes.DELETE_WHITELISTED_ACCOUNT:
      return {
        ...state,
        whitelistedAccounts: state.whitelistedAccounts.filter(acc => acc.id !== action.payload),
      };

    case ActionTypes.SET_USER_TYPE:
      return {
        ...state,
        userType: action.payload,
      };

    case ActionTypes.SET_PLATFORM_LOGIN:
      return {
        ...state,
        isLoggedInFromPlatform: action.payload,
      };

    case ActionTypes.ADD_PENDING_REGISTRATION:
      return {
        ...state,
        pendingAccountRegistrations: [...state.pendingAccountRegistrations, action.payload],
      };

    case ActionTypes.SET_PENDING_REGISTRATIONS:
      // Also mark pending registrations as loaded when setting data
      const pendingDataLoaded = {
        ...state.dataLoaded,
        pendingRegistrations: true,
      };
      const pendingAllLoaded = Object.values(pendingDataLoaded).every(v => v === true);
      if (pendingAllLoaded) {
        console.log('[Loading] All data ready - stopping spinner');
      }
      return {
        ...state,
        pendingAccountRegistrations: action.payload,
        dataLoaded: pendingDataLoaded,
        isLoading: pendingAllLoaded ? false : state.isLoading,
      };

    case ActionTypes.APPROVE_PENDING_REGISTRATION:
      const approvedReg = state.pendingAccountRegistrations.find(r => r.id === action.payload);
      return {
        ...state,
        pendingAccountRegistrations: state.pendingAccountRegistrations.filter(r => r.id !== action.payload),
        whitelistedAccounts: approvedReg
          ? [...state.whitelistedAccounts, { ...approvedReg, status: 'whitelisted', approvedAt: new Date().toISOString() }]
          : state.whitelistedAccounts,
      };

    case ActionTypes.REJECT_PENDING_REGISTRATION:
      const rejectedReg = state.pendingAccountRegistrations.find(r => r.id === action.payload.id);
      return {
        ...state,
        pendingAccountRegistrations: state.pendingAccountRegistrations.filter(r => r.id !== action.payload.id),
        whitelistedAccounts: rejectedReg
          ? [...state.whitelistedAccounts, { ...rejectedReg, status: 'rejected', rejectedAt: new Date().toISOString(), rejectionReason: action.payload.reason }]
          : state.whitelistedAccounts,
      };

    case ActionTypes.DELETE_PENDING_REGISTRATION:
      return {
        ...state,
        pendingAccountRegistrations: state.pendingAccountRegistrations.filter(r => r.id !== action.payload),
      };

    case ActionTypes.ADD_TO_MANUAL_REVIEW:
      return {
        ...state,
        manualReviewQueue: [...state.manualReviewQueue, action.payload],
      };

    case ActionTypes.RESOLVE_MANUAL_REVIEW:
      return {
        ...state,
        manualReviewQueue: state.manualReviewQueue.filter(r => r.id !== action.payload),
      };

    // AI Settings cases
    case ActionTypes.SET_AI_CONFIGS:
      // Also mark AI settings as loaded when setting data
      const aiDataLoaded = {
        ...state.dataLoaded,
        aiSettings: true,
      };
      const aiAllLoaded = Object.values(aiDataLoaded).every(v => v === true);
      if (aiAllLoaded) {
        console.log('[Loading] All data ready - stopping spinner');
      }
      return {
        ...state,
        aiConfigs: action.payload,
        activeAIConfig: action.payload.find(c => c.isActive) || null,
        dataLoaded: aiDataLoaded,
        isLoading: aiAllLoaded ? false : state.isLoading,
      };

    case ActionTypes.ADD_AI_CONFIG:
      return {
        ...state,
        aiConfigs: [...state.aiConfigs, action.payload],
        activeAIConfig: action.payload.isActive ? action.payload : state.activeAIConfig,
      };

    case ActionTypes.UPDATE_AI_CONFIG:
      const updatedConfigs = state.aiConfigs.map(c =>
        c.id === action.payload.id ? { ...c, ...action.payload } : c
      );
      return {
        ...state,
        aiConfigs: updatedConfigs,
        activeAIConfig: updatedConfigs.find(c => c.isActive) || null,
      };

    case ActionTypes.DELETE_AI_CONFIG:
      const remainingConfigs = state.aiConfigs.filter(c => c.id !== action.payload);
      return {
        ...state,
        aiConfigs: remainingConfigs,
        activeAIConfig: state.activeAIConfig?.id === action.payload ? null : state.activeAIConfig,
      };

    case ActionTypes.SET_ACTIVE_AI_CONFIG:
      return {
        ...state,
        aiConfigs: state.aiConfigs.map(c => ({
          ...c,
          isActive: c.id === action.payload
        })),
        activeAIConfig: state.aiConfigs.find(c => c.id === action.payload) || null,
      };

    case ActionTypes.UPDATE_BROKER_USER:
      return {
        ...state,
        brokerUsers: state.brokerUsers.map(user =>
          user.id === action.payload.id ? { ...user, ...action.payload } : user
        ),
      };

    case ActionTypes.SET_DATA_LOADED:
      const newDataLoaded = {
        ...state.dataLoaded,
        [action.payload.key]: action.payload.value,
      };
      // Check if ALL data is loaded - if so, set isLoading to false
      const allDataLoaded = Object.values(newDataLoaded).every(v => v === true);
      const loadedCount = Object.values(newDataLoaded).filter(v => v).length;
      console.log(`[Loading] ${action.payload.key} loaded (${loadedCount}/4)`, allDataLoaded ? '- ALL DATA READY!' : '');
      return {
        ...state,
        dataLoaded: newDataLoaded,
        isLoading: allDataLoaded ? false : state.isLoading,
      };

    default:
      return state;
  }
}

// Provider Component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Subscribe to real-time Firebase updates
  useEffect(() => {
    let unsubscribeTransactions;
    let unsubscribePendingRegistrations;
    let unsubscribeWhitelistedAccounts;
    let unsubscribeAISettings;

    const initFirebase = async () => {
      console.log('[Firebase] Starting data subscriptions...');
      const startTime = Date.now();

      try {
        // Subscribe to real-time transaction updates
        // Loading flag is handled directly in SET_TRANSACTIONS reducer
        unsubscribeTransactions = transactionService.subscribeToAll((transactions) => {
          dispatch({ type: ActionTypes.SET_TRANSACTIONS, payload: transactions || [] });
        });

        // Subscribe to real-time pending registrations updates
        // Loading flag is handled directly in SET_PENDING_REGISTRATIONS reducer
        unsubscribePendingRegistrations = pendingRegistrationService.subscribeToAll((registrations) => {
          dispatch({ type: ActionTypes.SET_PENDING_REGISTRATIONS, payload: registrations || [] });
        });

        // Subscribe to real-time whitelisted accounts updates
        // Loading flag is handled directly in SET_WHITELISTED_ACCOUNTS reducer
        unsubscribeWhitelistedAccounts = whitelistedAccountService.subscribeToAll((accounts) => {
          dispatch({ type: ActionTypes.SET_WHITELISTED_ACCOUNTS, payload: accounts || [] });
        });

        // Load brokers (non-blocking)
        brokerService.getAll().then(brokersResult => {
          if (brokersResult.success && brokersResult.data.length > 0) {
            dispatch({ type: ActionTypes.SET_BROKERS, payload: brokersResult.data });
          }
        });

        // Load serial number for reference generation (non-blocking)
        serialNumberService.get().then(serialResult => {
          if (serialResult.success) {
            dispatch({ type: ActionTypes.SET_SERIAL_NUMBER, payload: serialResult.value });
            console.log('[Firebase] Serial number loaded:', serialResult.value);
          }
        });

        // Subscribe to real-time AI settings updates
        // Loading flag is handled directly in SET_AI_CONFIGS reducer
        unsubscribeAISettings = aiSettingsService.subscribeToAll((configs) => {
          dispatch({ type: ActionTypes.SET_AI_CONFIGS, payload: configs || [] });
        });

        dispatch({ type: ActionTypes.SET_FIREBASE_CONNECTED, payload: true });
        console.log(`[Firebase] All subscriptions setup in ${Date.now() - startTime}ms`);

        // Timeout fallback - reduced to 8 seconds (with caching, should never hit this)
        setTimeout(() => {
          console.log('[Firebase] Timeout fallback - force stopping loading after 8s');
          dispatch({ type: ActionTypes.SET_LOADING, payload: false });
        }, 8000);
      } catch (error) {
        console.log('Firebase not configured, using local state:', error.message);
        dispatch({ type: ActionTypes.SET_FIREBASE_CONNECTED, payload: false });
        // Force stop loading
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    };

    initFirebase();

    return () => {
      if (unsubscribeTransactions) unsubscribeTransactions();
      if (unsubscribePendingRegistrations) unsubscribePendingRegistrations();
      if (unsubscribeWhitelistedAccounts) unsubscribeWhitelistedAccounts();
      if (unsubscribeAISettings) unsubscribeAISettings();
    };
  }, []);

  // Load AI config from localStorage on mount (fallback for when Firebase isn't available)
  useEffect(() => {
    try {
      // Load all AI configs from localStorage
      const savedConfigs = localStorage.getItem('ai_api_configs');
      let parsedConfigs = [];
      if (savedConfigs) {
        parsedConfigs = JSON.parse(savedConfigs);
        if (parsedConfigs && parsedConfigs.length > 0) {
          console.log('[AppContext] Loaded AI configs from localStorage:', parsedConfigs.length);
        }
      }

      // Load active config from localStorage
      const savedActiveConfig = localStorage.getItem('ai_active_config_full');
      let activeConfig = null;
      if (savedActiveConfig) {
        activeConfig = JSON.parse(savedActiveConfig);
        console.log('[AppContext] Loaded active AI config from localStorage:', activeConfig?.id);
      }

      // Dispatch configs with active one marked
      if (parsedConfigs.length > 0) {
        // Mark the active config in the array
        const configsWithActive = parsedConfigs.map(c => ({
          ...c,
          isActive: activeConfig ? c.id === activeConfig.id : false
        }));
        dispatch({ type: ActionTypes.SET_AI_CONFIGS, payload: configsWithActive });
      }
    } catch (e) {
      console.error('[AppContext] Error loading AI configs from localStorage:', e);
    }
  }, []);

  // Actions
  const setLoading = useCallback((isLoading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: isLoading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  }, []);

  const setCurrentTransaction = useCallback((transaction) => {
    dispatch({ type: ActionTypes.SET_CURRENT_TRANSACTION, payload: transaction });
  }, []);

  const setVerificationStatus = useCallback((status) => {
    dispatch({ type: ActionTypes.SET_VERIFICATION_STATUS, payload: status });
  }, []);

  const setReferenceNumber = useCallback((refNumber) => {
    dispatch({ type: ActionTypes.SET_REFERENCE_NUMBER, payload: refNumber });
  }, []);

  const setOtpCode = useCallback((code, expiry) => {
    dispatch({ type: ActionTypes.SET_OTP_CODE, payload: { code, expiry } });
  }, []);

  const addTransaction = useCallback(async (transaction, brokerId = null) => {
    dispatch({ type: ActionTypes.ADD_TRANSACTION, payload: transaction });
    // Also save to Firebase (broker-specific collection)
    try {
      const effectiveBrokerId = brokerId || transaction.brokerId || transaction.brokerPortal;
      await transactionService.create(transaction, effectiveBrokerId);
    } catch (error) {
      console.log('Firebase save failed, using local state:', error.message);
    }
  }, []);

  const updateTransaction = useCallback(async (transaction, brokerId = null) => {
    dispatch({ type: ActionTypes.UPDATE_TRANSACTION, payload: transaction });
    // Also update in Firebase (broker-specific collection)
    try {
      if (transaction.id) {
        const effectiveBrokerId = brokerId || transaction.brokerId || transaction.brokerPortal;
        await transactionService.update(transaction.id, transaction, effectiveBrokerId);
      }
    } catch (error) {
      console.log('Firebase update failed, using local state:', error.message);
    }
  }, []);

  // Upload receipt to Firebase Storage
  const uploadReceipt = useCallback(async (transactionId, file) => {
    try {
      const result = await storageService.uploadReceipt(transactionId, file);
      if (result.success) {
        await transactionService.update(transactionId, { receiptUrl: result.url });
        return result;
      }
      return { success: false, error: 'Upload failed' };
    } catch (error) {
      console.log('Receipt upload failed:', error.message);
      return { success: false, error: error.message };
    }
  }, []);

  const loginAdmin = useCallback((user) => {
    dispatch({ type: ActionTypes.LOGIN_ADMIN, payload: user });
  }, []);

  const logoutAdmin = useCallback(() => {
    dispatch({ type: ActionTypes.LOGOUT_ADMIN });
  }, []);

  const addBroker = useCallback((broker) => {
    dispatch({ type: ActionTypes.ADD_BROKER, payload: broker });
  }, []);

  const updateBroker = useCallback((broker) => {
    dispatch({ type: ActionTypes.UPDATE_BROKER, payload: broker });
  }, []);

  const addNotification = useCallback((notification) => {
    const id = Date.now();
    dispatch({ type: ActionTypes.ADD_NOTIFICATION, payload: { ...notification, id } });
    // Auto-clear after 5 seconds
    setTimeout(() => {
      dispatch({ type: ActionTypes.CLEAR_NOTIFICATION, payload: id });
    }, 5000);
  }, []);

  const resetUserSession = useCallback(() => {
    dispatch({ type: ActionTypes.RESET_USER_SESSION });
  }, []);

  const addWhitelistedAccount = useCallback((account) => {
    dispatch({ type: ActionTypes.ADD_WHITELISTED_ACCOUNT, payload: account });
  }, []);

  const setWhitelistedAccounts = useCallback((accounts) => {
    dispatch({ type: ActionTypes.SET_WHITELISTED_ACCOUNTS, payload: accounts });
  }, []);

  const updateWhitelistedAccount = useCallback((account) => {
    dispatch({ type: ActionTypes.UPDATE_WHITELISTED_ACCOUNT, payload: account });
  }, []);

  const deleteWhitelistedAccount = useCallback(async (accountId, brokerId = null) => {
    dispatch({ type: ActionTypes.DELETE_WHITELISTED_ACCOUNT, payload: accountId });
    // Also delete from Firebase (broker-specific collection)
    try {
      const result = await whitelistedAccountService.delete(accountId, brokerId);
      if (result.success) {
        console.log('Account deleted from Firebase:', accountId, 'broker:', brokerId);
      } else {
        console.error('Firebase delete failed:', result.error);
      }
      return result;
    } catch (error) {
      console.error('Error deleting account from Firebase:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const setUserType = useCallback((type) => {
    dispatch({ type: ActionTypes.SET_USER_TYPE, payload: type });
  }, []);

  const setPlatformLogin = useCallback((isLoggedIn) => {
    dispatch({ type: ActionTypes.SET_PLATFORM_LOGIN, payload: isLoggedIn });
  }, []);

  const addPendingRegistration = useCallback(async (registration, brokerId = null) => {
    console.log('[AppContext] Adding pending registration:', registration);
    console.log('[AppContext] Registration ID:', registration.id);
    console.log('[AppContext] Registration status:', registration.status);
    console.log('[AppContext] Registration bankName:', registration.bankName);
    console.log('[AppContext] Broker ID:', brokerId || registration.brokerId);

    dispatch({ type: ActionTypes.ADD_PENDING_REGISTRATION, payload: registration });

    // Also save to Firebase (broker-specific collection)
    try {
      const effectiveBrokerId = brokerId || registration.brokerId;
      console.log('[AppContext] Calling pendingRegistrationService.create with broker:', effectiveBrokerId);
      const result = await pendingRegistrationService.create(registration, effectiveBrokerId);
      console.log('[AppContext] Firebase create result:', result);
      if (result.success) {
        console.log('[AppContext] SUCCESS - Pending registration saved to Firebase:', registration.id, 'broker:', effectiveBrokerId);
      } else {
        console.error('[AppContext] FAILED - Firebase returned error:', result.error);
      }
      return result;
    } catch (error) {
      console.error('[AppContext] EXCEPTION - Firebase save failed:', error.message);
      console.error('[AppContext] Full error:', error);
      throw error;
    }
  }, []);

  const approvePendingRegistration = useCallback(async (registrationId, brokerId = null) => {
    dispatch({ type: ActionTypes.APPROVE_PENDING_REGISTRATION, payload: registrationId });
    // Also update in Firebase (broker-specific collection)
    try {
      await pendingRegistrationService.approve(registrationId, brokerId);
      console.log('Registration approved in Firebase:', registrationId, 'broker:', brokerId);
    } catch (error) {
      console.log('Firebase approve failed:', error.message);
    }
  }, []);

  const rejectPendingRegistration = useCallback(async (registrationId, reason, brokerId = null) => {
    dispatch({ type: ActionTypes.REJECT_PENDING_REGISTRATION, payload: { id: registrationId, reason } });
    // Also update in Firebase (broker-specific collection)
    try {
      await pendingRegistrationService.reject(registrationId, reason, brokerId);
      console.log('Registration rejected in Firebase:', registrationId, 'broker:', brokerId);
    } catch (error) {
      console.log('Firebase reject failed:', error.message);
    }
  }, []);

  const deletePendingRegistration = useCallback(async (registrationId, brokerId = null) => {
    dispatch({ type: ActionTypes.DELETE_PENDING_REGISTRATION, payload: registrationId });
    // Also delete from Firebase (broker-specific collection)
    try {
      const result = await pendingRegistrationService.delete(registrationId, brokerId);
      if (result.success) {
        console.log('Pending registration deleted from Firebase:', registrationId, 'broker:', brokerId);
      } else {
        console.error('Firebase delete failed:', result.error);
      }
      return result;
    } catch (error) {
      console.error('Error deleting pending registration from Firebase:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const addToManualReview = useCallback((item) => {
    dispatch({ type: ActionTypes.ADD_TO_MANUAL_REVIEW, payload: item });
  }, []);

  const resolveManualReview = useCallback((itemId) => {
    dispatch({ type: ActionTypes.RESOLVE_MANUAL_REVIEW, payload: itemId });
  }, []);

  // Generate unique reference number
  // Format: PREFIX-DDMMYYYY-SerialNumber (e.g., SV-08012026-001 or JM-08012026-001)
  // Prefix is based on broker: SV for Savexa, JM for JustMarkets
  // Persists serial number to Firebase for continuity across sessions
  // Each broker has its own serial number counter (separate databases)
  const generateReferenceNumber = useCallback(async (brokerPrefix = 'SV', brokerId = null) => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const dateStr = `${day}${month}${year}`;

    // Determine broker ID from prefix if not provided
    const effectiveBrokerId = brokerId || (brokerPrefix === 'JM' ? 'justmarkets' : 'savexa');

    // Increment serial number in Firebase (broker-specific) and get new value
    let newSerialNumber;
    try {
      const result = await serialNumberService.increment(effectiveBrokerId);
      if (result.success) {
        newSerialNumber = result.value;
        dispatch({ type: ActionTypes.SET_SERIAL_NUMBER, payload: newSerialNumber });
      } else {
        // Fallback to local increment if Firebase fails
        dispatch({ type: ActionTypes.INCREMENT_SERIAL_NUMBER });
        newSerialNumber = state.lastSerialNumber + 1;
      }
    } catch (error) {
      console.error('Error incrementing serial number in Firebase:', error);
      // Fallback to local increment
      dispatch({ type: ActionTypes.INCREMENT_SERIAL_NUMBER });
      newSerialNumber = state.lastSerialNumber + 1;
    }

    const serialNumber = String(newSerialNumber).padStart(3, '0');

    // Use broker-specific prefix (SV for Savexa, JM for JustMarkets)
    const refNumber = `${brokerPrefix}-${dateStr}-${serialNumber}`;
    dispatch({ type: ActionTypes.SET_REFERENCE_NUMBER, payload: refNumber });
    return refNumber;
  }, [state.lastSerialNumber]);

  // Get whitelisted accounts for a specific CNIC
  const getWhitelistedAccountsByCnic = useCallback((cnic) => {
    return state.whitelistedAccounts.filter(acc => acc.cnic === cnic && acc.status === 'whitelisted');
  }, [state.whitelistedAccounts]);

  // Check if user has whitelisted accounts
  const hasWhitelistedAccounts = useCallback((cnic) => {
    return state.whitelistedAccounts.some(acc => acc.cnic === cnic && acc.status === 'whitelisted');
  }, [state.whitelistedAccounts]);

  // AI Settings Actions
  const addAIConfig = useCallback(async (config) => {
    dispatch({ type: ActionTypes.ADD_AI_CONFIG, payload: config });
    try {
      await aiSettingsService.create(config);
      console.log('AI config saved to Firebase:', config.id);
    } catch (error) {
      console.log('Firebase AI config save failed:', error.message);
    }
  }, []);

  const updateAIConfig = useCallback(async (config) => {
    dispatch({ type: ActionTypes.UPDATE_AI_CONFIG, payload: config });
    try {
      await aiSettingsService.update(config.id, config);
      console.log('AI config updated in Firebase:', config.id);
    } catch (error) {
      console.log('Firebase AI config update failed:', error.message);
    }
  }, []);

  const deleteAIConfig = useCallback(async (configId) => {
    dispatch({ type: ActionTypes.DELETE_AI_CONFIG, payload: configId });
    try {
      await aiSettingsService.delete(configId);
      console.log('AI config deleted from Firebase:', configId);
    } catch (error) {
      console.log('Firebase AI config delete failed:', error.message);
    }
  }, []);

  const setActiveAIConfig = useCallback(async (configId) => {
    dispatch({ type: ActionTypes.SET_ACTIVE_AI_CONFIG, payload: configId });
    try {
      await aiSettingsService.setActive(configId);
      console.log('Active AI config set in Firebase:', configId);
    } catch (error) {
      console.log('Firebase set active AI config failed:', error.message);
    }
  }, []);

  // Get the active AI configuration (also checks localStorage as fallback)
  const getActiveAIConfig = useCallback(() => {
    // If we have it in state, return that
    if (state.activeAIConfig) {
      return state.activeAIConfig;
    }
    // Fallback: check localStorage
    try {
      const savedConfig = localStorage.getItem('ai_active_config_full');
      if (savedConfig) {
        return JSON.parse(savedConfig);
      }
    } catch (e) {
      console.error('Error reading AI config from localStorage:', e);
    }
    return null;
  }, [state.activeAIConfig]);

  // Update broker user (for CNIC extraction data)
  const updateBrokerUser = useCallback((userData) => {
    dispatch({ type: ActionTypes.UPDATE_BROKER_USER, payload: userData });
    // Note: In production, this would also sync to Firebase/backend
    console.log('Broker user updated:', userData.id);
  }, []);

  // Delete all old data from default (non-prefixed) Firebase collections
  // This is useful when migrating to broker-specific collections
  const deleteOldData = useCallback(async () => {
    console.log('[Cleanup] Starting deletion of old default collections...');
    try {
      // First get counts to show what will be deleted
      const counts = await cleanupService.getOldCollectionCounts();
      console.log('[Cleanup] Documents to delete:', counts);

      if (counts.total === 0) {
        console.log('[Cleanup] No old data found to delete.');
        return { success: true, message: 'No old data found to delete', totalDeleted: 0 };
      }

      // Perform the deletion
      const result = await cleanupService.deleteOldDefaultCollections();
      console.log('[Cleanup] Deletion complete:', result);
      return result;
    } catch (error) {
      console.error('[Cleanup] Error during cleanup:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Expose cleanup function globally for console access
  useEffect(() => {
    window.deleteOldData = deleteOldData;
    window.getOldDataCounts = cleanupService.getOldCollectionCounts;
    console.log('[App] Cleanup functions available: window.deleteOldData(), window.getOldDataCounts()');
    return () => {
      delete window.deleteOldData;
      delete window.getOldDataCounts;
    };
  }, [deleteOldData]);

  const value = {
    ...state,
    setLoading,
    setError,
    setCurrentTransaction,
    setVerificationStatus,
    setReferenceNumber,
    setOtpCode,
    addTransaction,
    updateTransaction,
    uploadReceipt,
    loginAdmin,
    logoutAdmin,
    addBroker,
    updateBroker,
    addNotification,
    resetUserSession,
    addWhitelistedAccount,
    setWhitelistedAccounts,
    updateWhitelistedAccount,
    deleteWhitelistedAccount,
    setUserType,
    setPlatformLogin,
    addPendingRegistration,
    approvePendingRegistration,
    rejectPendingRegistration,
    deletePendingRegistration,
    addToManualReview,
    resolveManualReview,
    generateReferenceNumber,
    getWhitelistedAccountsByCnic,
    hasWhitelistedAccounts,
    PAKISTAN_BANKS,
    // AI Settings
    addAIConfig,
    updateAIConfig,
    deleteAIConfig,
    setActiveAIConfig,
    getActiveAIConfig,
    // Broker Users
    updateBrokerUser,
    // Cleanup
    deleteOldData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// Hook
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

export default AppContext;
